import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { IdNameList } from "../../../../api/services/common/models";
import { UpdateProjectKeyDetailsPayload } from "../../../../api/services/project/model";
import { useUpdateProjectKeyDetailsMutation } from "../../../../api/services/project/projectApis";
import { ReactComponent as EditIcon } from "../../../../assets/images/edit.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiLink,
  MuiModal,
  WarningConfirmationModal,
} from "../../../../components";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS } from "../../../../helpers/constants";
import { RefDropdownHandle } from "../../../../helpers/types/common";
import {
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import useAccordion from "../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { keyDetailsSelector } from "../../../../redux/slices/project/projectSelector";
import { CancelFieldModal } from "../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import {
  CreateProjectFormKey,
  CreateProjectFormTypeValues,
} from "../../modelsAndSchemas/Models";
import { keyProjectDetailsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateProjectKeyInput } from "../createProjectKeyInput/CreateProjectKeyInput";
import { CreateProjectGeneralDetailWrapper } from "./CreateProjectGeneralDetail.style";
import { KeyDetail } from "./keyDetail/KeyDetail";

interface CreateProjectKeyInputDetailProps {
  pageId: string;
  projectId?: string;
  isViewProject?: boolean;
  refetchProjectKeyDetailApi?: (id: string) => void;
}

const accordionStatus = {
  generalDetail: true,
};
export const CreateProjectGeneralDetail: FC<
  CreateProjectKeyInputDetailProps
> = ({ pageId, isViewProject, projectId = "", refetchProjectKeyDetailApi }) => {
  const dispatch = useDispatch();
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { fetchLabel, fetchComponentTitle, commonLabels } = useSiteCore();
  const { handleIndividualAccordionChange } = useAccordion();
  const [showModifyConfirmationModal, setShowModifyConfirmationModal] =
    useState(false);
  const poNumberDropdownRef = useRef<RefDropdownHandle>(null);
  const productLinesDropdownRef = useRef<RefDropdownHandle>(null);

  const projectKeyDetails = useSelector(keyDetailsSelector);

  const {
    reset,
    handleSubmit,
    getValues,
    formState: { isDirty, dirtyFields, errors },
    setValue,
    getFieldState,
  } = useFormContext();

  const {
    mutateAsync: updateProjectApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateProjectKeyDetailsMutation();

  useEffect(() => {
    if (isUpdateError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isUpdateError]);

  useEffect(() => {
    if (isUpdatePending) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isUpdatePending]);

  useEffect(() => {
    if (isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            keyProjectDetailsSiteCoreKeys.keyProjectDetails,
            keyProjectDetailsSiteCoreKeys.projectUpdatedSuccessMessage,
          ),
        }),
      );
      setShowModifyModal(false);
      refetchProjectKeyDetailApi?.(projectId);
    }
  }, [isUpdateSuccess]);

  const openModifyModal = () => {
    setShowModifyModal(true);
    if (isNotEmpty(projectKeyDetails)) {
      const {
        client,
        billingOffice,
        clientBusinessUnit,
        clientCostCentre,
        poNumber,
        salesOffice,
        productLine,
        projectFramework,
        projectName,
        primaryDeliveryCountry,
        primaryProductLine,
        primaryDeliveryOffice,
        dossierNumber,
        actualClient,
      } = projectKeyDetails!;
      const productLineIds =
        productLine?.map((item: IdNameList) => item.id) ?? [];
      const poNumberIds = poNumber?.map((item: IdNameList) => item.id) ?? [];

      resetFormValues({
        client: client?.id,
        clientName: client?.name,
        projectName: projectName,
        productLines: productLineIds,
        primaryProductLine: primaryProductLine.id,
        poNumbers: poNumberIds,
        clientBusinessUnit: clientBusinessUnit,
        clientCostCenter: clientCostCentre,
        projectFramework: projectFramework.id,
        salesOfficeId: salesOffice?.id,
        billingOffice: billingOffice?.name,
        primaryDeliveryCountry: primaryDeliveryCountry.name,
        primaryDeliveryOffice: primaryDeliveryOffice.id,
        dossierNumber: getValueOrBlank(dossierNumber),
        actualClientId: getValueOrBlank(actualClient?.id),
      });
    }
  };

  const checkValidationAndUpdate = async () => {
    // Check if primaryDeliveryOffice or primaryProductLine field has error
    const primaryDeliveryOfficeState = getFieldState(
      CreateProjectFormKey.primaryDeliveryOffice,
    );
    const primaryProductLineState = getFieldState(
      CreateProjectFormKey.primaryProductLine,
    );
    if (
      primaryDeliveryOfficeState.error ||
      primaryProductLineState.error ||
      (errors && Object.keys(errors).length > 0)
    ) {
      return;
    }

    if (dirtyFields && Object.keys(dirtyFields).length > 0) {
      setShowModifyConfirmationModal(true);
      setShowModifyModal(false);
    } else {
      handleSubmit(handleUpdateDetail)();
    }
  };

  const handleUpdateButtonClick = () => {
    setTimeout(() => {
      checkValidationAndUpdate();
    }, 0);
  };

  const handleUpdateDetail = () => {
    const {
      projectName,
      productLines: productLineId,
      poNumbers,
      clientBusinessUnit,
      clientCostCenter: clientCostCentre,
      projectFramework: projectFrameworkId,
      primaryProductLine,
      primaryDeliveryOffice,
      salesOfficeId,
      dossierNumber,
      actualClientId,
    } = getValues();

    const updateProjectParams: UpdateProjectKeyDetailsPayload = {
      projectName,
      productLineId,
      primaryProductLineId: primaryProductLine,
      purchaseOrderId: isNotEmpty(poNumbers) ? poNumbers : [],
      clientBusinessUnit,
      clientCostCentre,
      projectFrameworkId,
      projectId,
      primaryDeliveryOfficeId: getValueOrBlank(primaryDeliveryOffice),
      salesOfficeId: getValueOrBlank(salesOfficeId),
      dossierNumber: getValueOrBlank(dossierNumber),
      actualClientId: getValueOrBlank(actualClientId),
    };
    updateProjectApi(updateProjectParams);
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      setShowModifyModal(false);
    }
  };

  const handleCancelOk = () => {
    setShowModifyModal(false);
    setShowCancelModal(false);
  };

  const handleModifyConfirmationClick = () => {
    if (showModifyConfirmationModal) {
      setShowModifyConfirmationModal(false);
      handleSubmit(handleUpdateDetail)();
    }
  };

  const resetFormValues = (data: CreateProjectFormTypeValues) => {
    reset({
      [CreateProjectFormKey.client]: data?.client,
      [CreateProjectFormKey.clientName]: data?.clientName,
      [CreateProjectFormKey.projectName]: data?.projectName,
      [CreateProjectFormKey.productLines]: data?.productLines,
      [CreateProjectFormKey.primaryProductLine]: data?.primaryProductLine,
      [CreateProjectFormKey.poNumbers]: data?.poNumbers,
      [CreateProjectFormKey.clientBusinessUnit]: data?.clientBusinessUnit,
      [CreateProjectFormKey.clientCostCenter]: data?.clientCostCenter,
      [CreateProjectFormKey.projectFramework]: data?.projectFramework,
      [CreateProjectFormKey.salesOfficeId]: data?.salesOfficeId,
      [CreateProjectFormKey.billingOffice]: data?.billingOffice,
      [CreateProjectFormKey.primaryDeliveryCountry]:
        data?.primaryDeliveryCountry,
      [CreateProjectFormKey.primaryDeliveryOffice]: data?.primaryDeliveryOffice,
      [CreateProjectFormKey.dossierNumber]: getValueOrBlank(
        data?.dossierNumber,
      ),
      [CreateProjectFormKey.actualClientId]: getValueOrBlank(
        data?.actualClientId,
      ),
    });

    setValue(CreateProjectFormKey.client, data?.client, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const restoreValuesAndOpenModifyModal = () => {
    setShowModifyModal(true);
    if (isNotEmpty(getValues())) {
      const {
        client,
        clientName,
        projectName,
        productLines,
        poNumbers,
        clientBusinessUnit,
        clientCostCenter,
        projectFramework,
        salesOffice,
        billingOffice,
        primaryDeliveryCountry,
        primaryProductLine,
        primaryDeliveryOffice,
        dossierNumber,
        actualClientId,
      } = getValues();

      resetFormValues({
        client,
        clientName,
        projectName,
        productLines,
        primaryProductLine,
        poNumbers,
        clientBusinessUnit,
        clientCostCenter,
        projectFramework,
        salesOfficeId: salesOffice,
        billingOffice,
        primaryDeliveryCountry,
        primaryDeliveryOffice,
        dossierNumber,
        actualClientId,
      });
    }
  };

  const handleModifyConfirmationModalClose = () => {
    if (showModifyConfirmationModal) {
      setShowModifyConfirmationModal(false);
      restoreValuesAndOpenModifyModal();
    }
  };

  const modalTitle = useMemo(() => {
    const title = fetchLabel(
      keyProjectDetailsSiteCoreKeys.keyProjectDetails,
      keyProjectDetailsSiteCoreKeys.modifyKeyProjectDetailsPopupHeader,
    );
    if (isEmpty(projectKeyDetails?.projectKey)) {
      return title;
    }
    return `${title} - ${getValueOrBlank(projectKeyDetails?.projectKey)}`;
  }, [projectKeyDetails?.projectKey]);

  return (
    <CreateProjectGeneralDetailWrapper>
      <PermissionWrapper
        permission={MODULE_PERMISSIONS.PROJECT_VIEW_KEY_PROJECT_DETAILS}
      >
        <AccordionHeader
          title={
            <AccordionHeaderText
              label={fetchComponentTitle(
                keyProjectDetailsSiteCoreKeys.keyProjectDetails,
              )}
            />
          }
          onChange={() => handleIndividualAccordionChange("generalDetail")}
          isExpanded={accordionStatus.generalDetail}
        >
          {!isViewProject && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.PROJECT_EDIT_KEY_PROJECT_DETAILS}
            >
              <MuiBox className="modify-detail">
                <MuiLink
                  className="mui-link link"
                  onClick={openModifyModal}
                  id="modify-link"
                  data-testid="modify-link"
                >
                  <EditIcon />
                  {commonLabels.modify}
                </MuiLink>
              </MuiBox>
            </PermissionWrapper>
          )}
          <KeyDetail />
          {showModifyModal && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.PROJECT_EDIT_KEY_PROJECT_DETAILS}
            >
              <MuiModal
                onClose={handleCancelClick}
                title={modalTitle}
                open={showModifyModal}
                hasLargeContent
                primaryButtonTitle={commonLabels.update}
                onPrimaryButtonClick={handleUpdateButtonClick}
                secondaryButtonTitle={commonLabels.cancel}
                onSecondaryButtonClick={handleCancelClick}
              >
                <CreateProjectKeyInput
                  pageId={pageId}
                  isUpdateDetail={true}
                  productLinesDropdownRef={productLinesDropdownRef}
                  poNumberDropdownRef={poNumberDropdownRef}
                />
              </MuiModal>
            </PermissionWrapper>
          )}
          {showCancelModal && (
            <CancelFieldModal
              showModal={showCancelModal}
              handleModalClose={() => setShowCancelModal(false)}
              handlePrimaryButtonClick={handleCancelOk}
            />
          )}
          {showModifyConfirmationModal && (
            <WarningConfirmationModal
              show={showModifyConfirmationModal}
              modalTitle={commonLabels.confirmation}
              onClose={handleModifyConfirmationModalClose}
              secondaryButtonTitle={commonLabels.no}
              onPrimaryButtonClick={handleModifyConfirmationClick}
              labelContent={fetchLabel(
                keyProjectDetailsSiteCoreKeys.keyProjectDetails,
                keyProjectDetailsSiteCoreKeys.modifyKeyProjectDetailsPopupHeader,
              )}
            />
          )}
        </AccordionHeader>
      </PermissionWrapper>
    </CreateProjectGeneralDetailWrapper>
  );
};
